/**
 * Pure transform utilities that convert API coverage maps (keyed objects with
 * `count` / `percentage` entries) to the item arrays consumed by
 * AttributeComparisonList and AttributeScoreList.
 *
 * Kept at the global utils layer so any feature can reuse the same
 * normalisation logic without duplicating it in feature-level mappers.
 */

import type { AttributeComparisonItem } from '~/components/common/attribute-comparison-list';
import type { AttributeItem } from '~/components/common/attribute-score-list';

/** Shape shared by all three coverage maps on the batch-detail wire payload. */
export interface CoverageEntry {
  count: number;
  percentage: number;
}

export type CoverageMap = Record<string, CoverageEntry> | null | undefined;

/**
 * Transforms an occasion coverage map into items for AttributeComparisonList.
 * Each occasion's percentage becomes `afterValue`; `maxValue` is always 100
 * since the values are already percentages. `beforeMap` (the pre-enrichment
 * baseline, keyed by the same occasion name) supplies `beforeValue` — defaults
 * to 0 when the backend hasn't surfaced a baseline for that occasion.
 */
export function occasionCoverageToComparison(
  map: CoverageMap,
  beforeMap?: CoverageMap,
): {
  items: AttributeComparisonItem[];
  maxValue: number;
} {
  if (!map) return { items: [], maxValue: 100 };
  const items = Object.entries(map).map(([name, entry]) => ({
    title: name,
    beforeValue: Math.round(beforeMap?.[name]?.percentage ?? 0),
    afterValue: Math.round(entry.percentage),
  }));
  return { items, maxValue: 100 };
}

/**
 * Transforms a trend coverage map into items for AttributeComparisonList.
 * Each trend's absolute `count` becomes `afterValue`; `maxValue` is derived
 * from the largest count across BOTH before + after so the bars stay on the
 * same scale. `beforeMap` (pre-enrichment baseline) supplies `beforeValue`,
 * defaulting to 0 when no baseline exists for that trend.
 */
export function trendCoverageToComparison(
  map: CoverageMap,
  beforeMap?: CoverageMap,
): {
  items: AttributeComparisonItem[];
  maxValue: number;
} {
  if (!map) return { items: [], maxValue: 1 };
  const items = Object.entries(map).map(([name, entry]) => ({
    title: name,
    beforeValue: beforeMap?.[name]?.count ?? 0,
    afterValue: entry.count,
  }));
  const maxValue =
    items.length > 0 ? Math.max(...items.map((i) => Math.max(i.afterValue, i.beforeValue)), 1) : 1;
  return { items, maxValue };
}

/**
 * Transforms a tag coverage map into attribute items for AttributeScoreList.
 * Coverage percentage (0–100) is scaled to the 0–10 score range used by
 * ScoreComparisonBar. Items are sorted descending by score.
 */
export function tagCoverageToAttributes(map: CoverageMap): AttributeItem[] {
  if (!map) return [];
  return Object.entries(map)
    .map(([name, entry]) => ({
      title: name,
      beforeScore: 0,
      afterScore: Math.min(10, Math.round(entry.percentage / 10)),
    }))
    .sort((a, b) => b.afterScore - a.afterScore);
}
